import React, { useEffect, useState, useRef } from 'react';
import { Award, Loader2, Ticket, Sparkles, Check } from 'lucide-react';
import { publicApi } from '../lib/api';

const TIER_META = {
  Bronze: { color: 'from-orange-200 to-orange-300', text: 'text-orange-900', icon: '🥉' },
  Argent: { color: 'from-slate-200 to-slate-300', text: 'text-slate-900', icon: '🥈' },
  Or: { color: 'from-amber-200 to-yellow-300', text: 'text-amber-900', icon: '🥇' },
  Platine: { color: 'from-purple-200 to-purple-300', text: 'text-purple-900', icon: '💎' }
};

/**
 * LoyaltyWidget: shows current points/tier for a given email.
 * Triggers fetch on email change (debounced).
 */
export const LoyaltyWidget = ({ email, onCouponPick }) => {
  const [data, setData] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setData(null); setCoupons([]); return;
    }
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const [l, c] = await Promise.all([
          publicApi.getLoyalty(email),
          publicApi.getCustomerCoupons(email).catch(() => [])
        ]);
        setData(l);
        setCoupons(c || []);
      } catch (e) {
        setData(null);
      } finally {
        setLoading(false);
      }
    }, 600);
    return () => clearTimeout(timerRef.current);
  }, [email]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Vérification de votre profil fidélité...
      </div>
    );
  }

  if (!data) return null;

  if (!data.exists) {
    return (
      <div className="flex items-start gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
        <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>
          <b>Bienvenue !</b> Cette réservation vous inscrit automatiquement au programme fidélité. 1 point offert par tranche de 1 000 F.
        </span>
      </div>
    );
  }

  const tier = TIER_META[data.tier] || TIER_META.Bronze;

  return (
    <div className="space-y-2">
      <div className={`bg-gradient-to-br ${tier.color} rounded-2xl p-4 ${tier.text}`}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest opacity-80 font-semibold flex items-center gap-1">
              <Award className="w-3 h-3" /> Palier {data.tier} {tier.icon}
            </div>
            <div className="text-2xl font-bold mt-1">{data.points} pts</div>
            <div className="text-xs opacity-80 mt-0.5">
              {data.trips_count || 0} balade{data.trips_count > 1 ? 's' : ''} · {data.orders_count || 0} commande{data.orders_count > 1 ? 's' : ''}
            </div>
          </div>
          {data.next_tier && data.points_to_next != null && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest opacity-70">Prochain palier</div>
              <div className="text-sm font-semibold">{data.next_tier}</div>
              <div className="text-[10px] opacity-70">dans {data.points_to_next} pts</div>
            </div>
          )}
        </div>
      </div>

      {coupons.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-2xl p-3">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-[#E89534] mb-2">
            <Ticket className="w-3.5 h-3.5" /> Vos codes promo disponibles
          </div>
          <div className="space-y-1.5">
            {coupons.map((c) => (
              <button
                key={c.code}
                type="button"
                onClick={() => onCouponPick && onCouponPick(c.code)}
                className="w-full flex items-center justify-between p-2 rounded-lg bg-white border border-orange-200 hover:border-[#F4A340] transition-colors group"
              >
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm font-bold text-slate-900">{c.code}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#F4A340] text-white font-bold">-{c.discount_percent}%</span>
                </div>
                <span className="text-[10px] text-slate-500 group-hover:text-[#E89534]">Appliquer →</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * CouponInput: input field with validate button. Shows applied discount inline.
 */
export const CouponInput = ({ email, appliedCode, discountPercent, onApply, onRemove }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const apply = async (codeToApply = code) => {
    if (!codeToApply) return;
    setLoading(true); setError('');
    try {
      const res = await publicApi.validateCoupon(codeToApply, email);
      onApply(res.code, res.discount_percent);
      setCode('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Code invalide');
    } finally { setLoading(false); }
  };

  // Auto-apply when parent picks
  useEffect(() => {
    if (appliedCode && !code) setCode('');
  }, [appliedCode, code]);

  if (appliedCode) {
    return (
      <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4 text-emerald-600" />
          <span className="text-sm font-semibold text-emerald-900">Code <b>{appliedCode}</b> appliqué</span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-600 text-white font-bold">-{discountPercent}%</span>
        </div>
        <button type="button" onClick={onRemove} className="text-xs text-slate-500 hover:text-red-600 underline">Retirer</button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex gap-2">
        <input
          type="text"
          placeholder="Code promo (optionnel)"
          value={code}
          onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(''); }}
          className="flex-1 px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none font-mono text-sm"
        />
        <button
          type="button"
          onClick={() => apply()}
          disabled={loading || !code}
          className="px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 disabled:bg-slate-200 text-white text-sm font-semibold transition-colors"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Appliquer'}
        </button>
      </div>
      {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
    </div>
  );
};

export const useCouponFromPick = () => {
  const [pickedCode, setPickedCode] = useState('');
  return { pickedCode, pick: setPickedCode, reset: () => setPickedCode('') };
};
